import { SMap } from '@avantia/client-and-server-utilities';
import { HpAnswerMap, HpAnswerValueTypes } from '@avantia/questionset-model';
import { HpClientQuestion, HpClientQuestionData, HpClientQuestionUI } from '../engineClient/types';
import { HpScoredLookupMap, HpScoringMap } from '../types/scoringTypes';

export interface HpRecalculateScoringMapResult {
  hasScoreChanged: boolean;
  scoringMap: HpScoringMap;
  totalScore: number;
}

export function calculateLookupScore(lookup: HpScoredLookupMap, value: HpAnswerValueTypes): number | undefined {
  if (!value) {
    return undefined;
  }

  const selectedKeys = Array.isArray(value) ? value : [value];
  let total = 0;
  let hasAnyScore = false;

  for (const key of selectedKeys) {
    const lookupItem = lookup[key as string];
    if (lookupItem && lookupItem.data && lookupItem.data.score !== undefined) {
      total += lookupItem.data.score;
      hasAnyScore = true;
    }
  }

  return hasAnyScore ? total : undefined;
}

export function getBaseQuestionId(questionId: string): string {
  const groupSuffixIndex = questionId.indexOf('~G');
  return groupSuffixIndex >= 0 ? questionId.substring(0, groupSuffixIndex) : questionId;
}

export function rebuildScoringMapFromAnswers(
  questions: SMap<HpClientQuestion>,
  answers: HpAnswerMap,
  questionsUI: SMap<HpClientQuestionUI>
): HpScoringMap {
  const scoringMap: HpScoringMap = {};

  for (const answerKey in answers) {
    const questionUI = questionsUI[answerKey];
    if (questionUI && !questionUI.isVisible) {
      continue;
    }

    const baseQuestionId = getBaseQuestionId(answerKey);
    const question = questions[baseQuestionId];
    if (!question) {
      continue;
    }

    const data = question.data as HpClientQuestionData;
    const { lookup } = data;
    if (!lookup) {
      continue;
    }

    const answer = answers[answerKey];
    if (!answer) {
      continue;
    }

    const answerValue = answer.customer;
    if (!answerValue) {
      continue;
    }

    const score = calculateLookupScore(lookup, answerValue);
    if (score !== undefined) {
      scoringMap[answerKey] = score;
    }
  }

  return scoringMap;
}

export function calculateTotalScore(scoringMap: HpScoringMap): number {
  let total = 0;
  for (const key in scoringMap) {
    total += scoringMap[key];
  }

  return total;
}

export function recalculateScoringMap(
  questions: SMap<HpClientQuestion>,
  answers: HpAnswerMap,
  currentScoringMap: HpScoringMap | undefined,
  questionsUI: SMap<HpClientQuestionUI>
): HpRecalculateScoringMapResult {
  const updatedScoringMap = rebuildScoringMapFromAnswers(questions, answers, questionsUI);
  const totalScore = calculateTotalScore(updatedScoringMap);
  const hasChanged = hasScoringMapChanged(updatedScoringMap, currentScoringMap || {});

  return { hasScoreChanged: hasChanged, scoringMap: updatedScoringMap, totalScore };
}

function hasScoringMapChanged(updated: HpScoringMap, current: HpScoringMap): boolean {
  const updatedKeys = Object.keys(updated);
  const currentKeys = Object.keys(current);
  if (updatedKeys.length !== currentKeys.length) {
    return true;
  }

  for (const key of updatedKeys) {
    if (updated[key] !== current[key]) {
      return true;
    }
  }

  return false;
}
